/**
 * Bulk actions over the CURRENT selection (Wave 4a, feature #6).
 *
 * Two destructive-ish mass operations driven by the Selection store (mode 'ids' or
 * 'filter'); both buttons are enabled only when something is selected (count > 0):
 *
 *   1. «Изменить поле у выбранных» — set one field to one value for the whole
 *      selection. ONE server request (POST /api/volunteers/bulk-update). Guarded by
 *      refs.bulkEditMaxTotal. Confirm step shows how many rows will change.
 *
 *   2. «Удалить выбранных» — bulk delete (POST /api/volunteers/bulk-delete) starts a
 *      resumable job; <JobProgress/> tracks it. Requires typing the exact count to
 *      arm the destructive button (safer than a checkbox for large deletes).
 *
 * The value editor is reused from <ValueEditor> so bulk-edit behaves exactly like
 * the filter builder's value picker (bool -> 'y'/'n', multiselect -> chips).
 */

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, CheckCircle2 } from "lucide-react";

import type { BulkSelection, BulkUpdateValues } from "@/api/client";
import {
  useBulkDeleteVolunteers,
  useBulkUpdateVolunteers,
  useRefs,
} from "@/api/hooks";
import { EDITABLE_FIELD_DEFS, FIELD_BY_KEY } from "@/lib/fields";
import { useSelection } from "@/store/selection";
import { FieldValuePicker, buildValue } from "@/components/FieldValuePicker";
import { JobProgress } from "@/components/touches/JobProgress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_BULK_EDIT_MAX = 20000;

/** Read the current Selection store as a wire BulkSelection (ids- or filter-mode). */
function useSelectionPayload(): BulkSelection {
  const mode = useSelection((s) => s.mode);
  const ids = useSelection((s) => s.ids);
  const filters = useSelection((s) => s.filters);
  const count = useSelection((s) => s.count);
  return React.useMemo<BulkSelection>(
    () =>
      mode === "filter"
        ? { mode: "filter", filters: filters ?? [], count } // count → planned_total (F37)
        : { mode: "ids", ids: ids ?? [] },
    [mode, ids, filters, count],
  );
}

export function BulkActions() {
  const count = useSelection((s) => s.count);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const hasSelection = count > 0;

  return (
    <div className="flex items-center gap-2" data-testid="bulk-actions">
      <Button
        variant="outline"
        size="sm"
        disabled={!hasSelection}
        onClick={() => setEditOpen(true)}
        data-testid="bulk-edit-open"
      >
        <Pencil className="h-4 w-4" />
        Изменить поле у выбранных
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={!hasSelection}
        onClick={() => setDeleteOpen(true)}
        className="text-destructive hover:text-destructive"
        data-testid="bulk-delete-open"
      >
        <Trash2 className="h-4 w-4" />
        Удалить выбранных
      </Button>

      {editOpen && <BulkEditDialog open={editOpen} onOpenChange={setEditOpen} />}
      {deleteOpen && <BulkDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} />}
    </div>
  );
}

// --- Bulk edit dialog ---------------------------------------------------------

export function BulkEditDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: refs } = useRefs();
  const count = useSelection((s) => s.count);
  const mode = useSelection((s) => s.mode);
  const selection = useSelectionPayload();
  const mutation = useBulkUpdateVolunteers();

  const [field, setField] = React.useState<string>(EDITABLE_FIELD_DEFS[0]?.field ?? "");
  const def = FIELD_BY_KEY[field];
  // Draft value as a string (comma-joined for multiselect), same as the filter draft.
  const [draft, setDraft] = React.useState<string>(def?.kind === "bool" ? "y" : "");
  const [confirming, setConfirming] = React.useState(false);

  function onFieldChange(next: string) {
    const nextDef = FIELD_BY_KEY[next];
    setField(next);
    setDraft(nextDef?.kind === "bool" ? "y" : "");
    setConfirming(false);
    mutation.reset();
  }

  const built = def ? buildValue(def, draft) : null;
  const hasValue = built != null;

  const limit = refs?.bulkEditMaxTotal ?? DEFAULT_BULK_EDIT_MAX;
  const overLimit = count > limit;

  const done = mutation.isSuccess;

  function apply() {
    if (!def || built == null) return;
    const values: BulkUpdateValues = { [def.field]: built };
    mutation.mutate({ selection, values });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          setConfirming(false);
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Изменить поле у выбранных</DialogTitle>
          <DialogDescription>
            {mode === "filter" ? (
              <>
                Вся выборка по фильтру:{" "}
                <span className="font-semibold tabular-nums text-foreground">
                  {count.toLocaleString("ru-RU")}
                </span>
              </>
            ) : (
              <>
                Выбрано:{" "}
                <span className="font-semibold tabular-nums text-foreground">
                  {count.toLocaleString("ru-RU")}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex items-center gap-2 rounded-md border border-green-600/40 bg-green-600/5 p-3 text-sm text-green-700 dark:text-green-300">
            <CheckCircle2 className="h-4 w-4" />
            <span>
              Изменено{" "}
              <span className="font-semibold tabular-nums">
                {(mutation.data?.count ?? count).toLocaleString("ru-RU")}
              </span>{" "}
              записей.
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            <FieldValuePicker
              fieldKey={field}
              draftValue={draft}
              onFieldChange={onFieldChange}
              onValueChange={(v) => {
                setDraft(v);
                setConfirming(false);
              }}
            />

            {overLimit && (
              <p className="text-xs text-destructive">
                Выбрано {count.toLocaleString("ru-RU")} — превышен предел массового
                изменения ({limit.toLocaleString("ru-RU")}). Сузьте выборку.
              </p>
            )}

            {confirming && hasValue && !overLimit && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-300">
                Будет изменено{" "}
                <span className="font-semibold tabular-nums">
                  {count.toLocaleString("ru-RU")}
                </span>{" "}
                {mode === "filter" ? "записей (вся выборка)" : "выбранных записей"}. Подтвердите.
              </p>
            )}

            {mutation.isError && (
              <p className="text-xs text-destructive">
                Не удалось применить изменение
                {mutation.error instanceof Error ? `: ${mutation.error.message}` : "."}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={() => onOpenChange(false)}>Закрыть</Button>
          ) : !confirming ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Отмена
              </Button>
              <Button
                disabled={!hasValue || overLimit}
                onClick={() => setConfirming(true)}
                data-testid="bulk-edit-next"
              >
                Далее
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={mutation.isPending}>
                Назад
              </Button>
              <Button
                disabled={!hasValue || overLimit || mutation.isPending}
                onClick={apply}
                data-testid="bulk-edit-apply"
              >
                {mutation.isPending ? <Spinner /> : null}
                Применить
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bulk delete dialog -------------------------------------------------------

function BulkDeleteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const count = useSelection((s) => s.count);
  const mode = useSelection((s) => s.mode);
  const selection = useSelectionPayload();
  const mutation = useBulkDeleteVolunteers();

  const [confirmInput, setConfirmInput] = React.useState("");
  const [jobId, setJobId] = React.useState<number | null>(null);

  // Armed only when the typed number exactly matches the selection count.
  const armed = confirmInput.trim() !== "" && Number(confirmInput.trim()) === count;

  function runDelete() {
    if (!armed) return;
    mutation.mutate(
      { selection },
      {
        onSuccess: (res) => {
          setJobId(res.job_id);
          // Drop the now-deleted ids from the selection so they don't ride along
          // into the next bulk operation (F20).
          useSelection.getState().clear();
        },
      },
    );
  }

  function close() {
    mutation.reset();
    setConfirmInput("");
    setJobId(null);
    onOpenChange(false);
  }

  const started = jobId != null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">Удалить выбранных</DialogTitle>
          <DialogDescription>
            Будет удалено{" "}
            <span className="font-semibold tabular-nums text-foreground">
              {count.toLocaleString("ru-RU")}
            </span>{" "}
            {mode === "filter" ? "(вся выборка по фильтру) " : ""}
            <span className="font-semibold text-destructive">безвозвратно</span>.
          </DialogDescription>
        </DialogHeader>

        {!started ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="bulk-delete-confirm" className="text-xs text-muted-foreground">
                Для подтверждения введите число удаляемых записей:{" "}
                <span className="font-semibold tabular-nums text-foreground">
                  {count.toLocaleString("ru-RU")}
                </span>
              </Label>
              <Input
                id="bulk-delete-confirm"
                inputMode="numeric"
                autoComplete="off"
                placeholder={String(count)}
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && armed) runDelete();
                }}
                className="h-9 tabular-nums"
                data-testid="bulk-delete-confirm-input"
              />
            </div>

            {mutation.isError && (
              <p className="text-xs text-destructive">
                Не удалось запустить удаление
                {mutation.error instanceof Error ? `: ${mutation.error.message}` : "."}
              </p>
            )}
          </div>
        ) : (
          <JobProgress
            jobId={jobId}
            onDone={() => {
              queryClient.invalidateQueries({ queryKey: ["volunteers"] });
              queryClient.invalidateQueries({ queryKey: ["volunteers-count"] });
            }}
          />
        )}

        <DialogFooter>
          {!started ? (
            <>
              <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
                Отмена
              </Button>
              <Button
                variant="destructive"
                disabled={!armed || mutation.isPending}
                onClick={runDelete}
                data-testid="bulk-delete-apply"
              >
                {mutation.isPending ? <Spinner /> : <Trash2 className="h-4 w-4" />}
                Удалить
              </Button>
            </>
          ) : (
            <Button onClick={close}>Закрыть</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
